/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faChartLine,
  faSearch,
  faFileAlt,
  faUser,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import './InternDashboardMobileHeader.css';

const InternDashboardMobileHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the avatar button and the dropdown
      const clickedOutsideAvatar = userMenuRef.current && !userMenuRef.current.contains(event.target);
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      if (clickedOutsideAvatar && clickedOutsideDropdown) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const [profilePhotoPath, setProfilePhotoPath] = useState(null);

  // Fetch profile photo path
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(buildApiUrl('/api/intern/profile'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.profilePhotoPath) {
          setProfilePhotoPath(response.data.profilePhotoPath);
        }
      } catch (error) {
        console.log('Error fetching profile photo:', error);
      }
    };
    fetchProfilePhoto();
  }, []);

  const getProfilePhotoUrl = (path) => {
    if (!path) return null;
    const normalizedPath = path.replace(/\\/g, '/');
    if (normalizedPath.startsWith('uploads/intern_photos/')) {
      const uploadPath = normalizedPath.replace('uploads/intern_photos/', 'intern_photos/');
      return buildApiUrl(`/api/users/uploads/${uploadPath}`);
    } else if (normalizedPath.startsWith('uploads/')) {
      const uploadPath = normalizedPath.replace('uploads/', '');
      return buildApiUrl(`/api/users/uploads/${uploadPath}`);
    } else if (normalizedPath.startsWith('/uploads/')) {
      const uploadPath = normalizedPath.replace('/uploads/', '');
      return buildApiUrl(`/api/users/uploads/${uploadPath}`);
    } else {
      return buildApiUrl(`/api/users/uploads/${normalizedPath}`);
    }
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email ? user.email.substring(0, 2).toUpperCase() : 'IN';
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'Intern';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Debug - log when dropdown state changes
  useEffect(() => {
    console.log('Mobile Header - showUserMenu:', showUserMenu);
  }, [showUserMenu]);

  return (
    <header className="intern-mobile-header">
      <div className="intern-mobile-header-left">
        <div className="intern-mobile-logo">
          <div className="intern-mobile-logo-icon">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <div className="intern-mobile-logo-text">
            <div className="intern-mobile-logo-name">INTERN HUB</div>
            <div className="intern-mobile-logo-subtitle">AksharJobs</div>
          </div>
        </div>
      </div>

      <div className="intern-mobile-header-right">
        {/* User Profile Dropdown */}
        <div className="intern-mobile-user-wrapper" ref={userMenuRef}>
          <div 
            className="intern-mobile-user-avatar"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {(() => {
              const photoUrl = user?.profileImage || (profilePhotoPath ? getProfilePhotoUrl(profilePhotoPath) : null);
              return photoUrl ? (
                <img src={photoUrl} alt={getUserName()} onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }} />
              ) : (
                <div className="intern-mobile-avatar-initials">{getUserInitials()}</div>
              );
            })()}
          </div>
        </div>

        {/* Dropdown rendered as portal to bypass overflow clipping */}
        {showUserMenu && ReactDOM.createPortal(
          <div 
            ref={dropdownRef} 
            style={{ 
              position: 'fixed',
              top: '64px',
              left: '8px',
              right: '8px',
              zIndex: 9999999,
              background: 'white',
              backgroundColor: 'white',
              border: '3px solid #667eea',
              borderRadius: '12px',
              boxShadow: '0 10px 50px rgba(0, 0, 0, 0.5)',
              display: 'block',
              visibility: 'visible',
              opacity: 1,
              pointerEvents: 'auto',
              overflow: 'visible'
            }}>
            {console.log('DROPDOWN IS RENDERING!')}
            {/* User Info */}
            <div style={{
              display: 'flex',
              padding: '16px 20px',
              background: 'white',
              backgroundColor: 'white',
              borderBottom: '1px solid #f1f5f9',
              gap: '12px',
              alignItems: 'center',
              visibility: 'visible',
              opacity: 1
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                flexShrink: 0,
                display: 'flex',
                visibility: 'visible',
                opacity: 1
              }}>
                {(() => {
                  const photoUrl = user?.profileImage || (profilePhotoPath ? getProfilePhotoUrl(profilePhotoPath) : null);
                  return photoUrl ? (
                    <img src={photoUrl} alt={getUserName()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }} />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 600
                    }}>{getUserInitials()}</div>
                  );
                })()}
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                visibility: 'visible',
                opacity: 1
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1e293b',
                  display: 'block',
                  visibility: 'visible',
                  opacity: 1
                }}>{getUserName()}</div>
                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  display: 'block',
                  visibility: 'visible',
                  opacity: 1
                }}>{user?.email}</div>
              </div>
            </div>

            <div style={{
              height: '1px',
              background: '#e2e8f0',
              margin: '8px 0',
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}></div>

            {/* Navigation Buttons */}
            <div style={{
              background: 'white',
              backgroundColor: 'white',
              padding: '0',
              minHeight: '300px',
              display: 'block',
              visibility: 'visible',
              opacity: 1,
              overflow: 'visible'
            }}>
              <div 
                onClick={() => { navigate('/intern-dashboard'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faChartLine} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>Dashboard</span>
              </div>

              <div 
                onClick={() => { navigate('/intern-dashboard#internships'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faSearch} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>Browse Internships</span>
              </div>

              <div 
                onClick={() => { navigate('/intern-dashboard#applications'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faFileAlt} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>My Applications</span>
              </div>

              <div 
                onClick={() => { navigate('/intern-dashboard#profile'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faUser} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>My Profile</span>
              </div>

              <div 
                onClick={() => { navigate('/intern-dashboard#notifications'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faBell} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>Notifications</span>
              </div>

              <div 
                onClick={() => { navigate('/intern-dashboard#messages'); setShowUserMenu(false); }}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#667eea', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#334155', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>Messages</span>
              </div>

              <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0', display: 'block', visibility: 'visible', opacity: 1 }}></div>

              <div 
                onClick={handleLogout}
                style={{ 
                  display: 'flex', 
                  padding: '14px 20px', 
                  minHeight: '48px', 
                  background: 'white', 
                  backgroundColor: 'white',
                  gap: '12px', 
                  alignItems: 'center', 
                  cursor: 'pointer', 
                  borderTop: '1px solid #e2e8f0',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} style={{ color: '#dc2626', fontSize: '16px', width: '20px', height: '20px', flexShrink: 0, display: 'inline-block', visibility: 'visible', opacity: 1 }} />
                <span style={{ color: '#dc2626', fontSize: '15px', fontWeight: 500, flex: 1, display: 'inline-block', visibility: 'visible', opacity: 1 }}>Logout</span>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </header>
  );
};

export default InternDashboardMobileHeader;

